// WebCrypto HMAC-SHA256 helpers.
//
// 必须与 gateway/internal/auth/hmac.go 字节对齐。Go 侧:
//   signing_string = ts || "\n" || nonce || "\n" || method || "\n" || path || "\n" || hex(sha256(body))
//   sig            = hex(hmac_sha256(token, signing_string))
//
// 拍板的几个细节（Day-2 验过 6 个攻击场景）：
//   1. ts 是十进制字符串，不是 RFC3339；GW 接受 ±300s 偏差
//   2. nonce 32 hex chars（16 bytes random）；GW 用 SQLite UNIQUE 防重放
//   3. body 即使为空也要 sha256("")，hash 是 hex lowercase
//   4. 所有 hex 都是 lowercase；HMAC 用 raw bytes，最后输出 hex

const enc = new TextEncoder();

function bytesToHex(buf: ArrayBuffer | ArrayBufferLike): string {
  const b = new Uint8Array(buf as ArrayBuffer);
  let s = "";
  for (let i = 0; i < b.length; i++) {
    s += b[i].toString(16).padStart(2, "0");
  }
  return s;
}

async function sha256Hex(data: Uint8Array): Promise<string> {
  // TS5 把 Uint8Array<ArrayBufferLike> 收紧到不直接兼容 BufferSource，强制裁回 ArrayBuffer 视图
  const buf = await crypto.subtle.digest("SHA-256", data.buffer.slice(data.byteOffset, data.byteOffset + data.byteLength) as ArrayBuffer);
  return bytesToHex(buf);
}

async function hmacSha256Hex(keyBytes: Uint8Array, msg: string): Promise<string> {
  // HMAC key 用 raw bytes import；token 是 ASCII 字符串（UTF-8 字节）
  const keyBuf = keyBytes.buffer.slice(
    keyBytes.byteOffset,
    keyBytes.byteOffset + keyBytes.byteLength
  ) as ArrayBuffer;
  const key = await crypto.subtle.importKey(
    "raw",
    keyBuf,
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"]
  );
  const msgBytes = enc.encode(msg);
  const msgBuf = msgBytes.buffer.slice(
    msgBytes.byteOffset,
    msgBytes.byteOffset + msgBytes.byteLength
  ) as ArrayBuffer;
  const sig = await crypto.subtle.sign("HMAC", key, msgBuf);
  return bytesToHex(sig);
}

function genNonce(): string {
  const b = new Uint8Array(16);
  crypto.getRandomValues(b);
  return bytesToHex(b.buffer);
}

export interface SignedHeaders {
  ts: string;
  nonce: string;
  sig: string;
  authHeaderValue: string; // 直接塞 X-Auth-Ear
  bodyHash: string;        // 调试时方便核
}

/** 给一个 HTTP 请求签名（phone HMAC，走 X-Auth-Ear header）。 */
export async function signRequest(
  deviceId: string,
  token: string,
  method: string,
  path: string,
  body: string | Uint8Array | null
): Promise<SignedHeaders> {
  const tokenBytes = enc.encode(token);
  const ts = Math.floor(Date.now() / 1000).toString();
  const nonce = genNonce();

  // body hash（即便 body 为空也是 sha256("")）
  const bodyBytes =
    body == null ? new Uint8Array(0) : typeof body === "string" ? enc.encode(body) : body;
  const bodyHash = await sha256Hex(bodyBytes);

  const signing = `${ts}\n${nonce}\n${method}\n${path}\n${bodyHash}`;
  const sig = await hmacSha256Hex(tokenBytes, signing);

  // X-Auth-Ear 格式与 GW 解析对齐：device_id=<id>,ts=<ts>,nonce=<n>,sig=<s>
  const authHeaderValue = `device_id=${deviceId},ts=${ts},nonce=${nonce},sig=${sig}`;
  return { ts, nonce, sig, authHeaderValue, bodyHash };
}

/** 给 WSS dial-in 签名（query string 形式，因为 WS 不能加自定义 header）。 */
export async function signWsHandshake(
  deviceId: string,
  token: string,
  path: string
): Promise<string> {
  const tokenBytes = enc.encode(token);
  const ts = Math.floor(Date.now() / 1000).toString();
  const nonce = genNonce();
  const bodyHash = await sha256Hex(new Uint8Array(0));
  const signing = `${ts}\n${nonce}\nGET\n${path}\n${bodyHash}`;
  const sig = await hmacSha256Hex(tokenBytes, signing);
  const qs = new URLSearchParams({ device_id: deviceId, ts, nonce, sig });
  return qs.toString();
}
